/**
 * NestedTRowForm - Form for nested (interleaving/zipper) T-hangar template
 *
 * Two rows of T-shaped units with tails interlocking in a shared middle zone.
 * Row A: N units facing south. Row B: N-1 units facing north, shifted half-pitch.
 */

import { useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import {
  nestedTHangarParamsSchema,
  DEFAULT_NESTED_T_HANGAR_VALUES,
  type NestedTHangarParams,
  type EndSpaceMode,
  calculateStepWidth,
  calculateNestedUnitPitch,
  calculateTShapeArea,
} from '@/lib/templates/nested-t-hangar/params'
import {
  calculateNestedTHangarLayout,
  getRowBCount,
} from '@/lib/templates/nested-t-hangar/calculations'
import type { DoorType, UnitDoorsConfig } from '@/lib/templates/types'
import { createDefaultTHangarDoorSpec } from '@/lib/templates/standard-t-hangar/params'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'
import { THangarUnitDoorEditor } from './THangarUnitDoorEditor'
import { RemoveAreaButton } from '../shared/RemoveAreaButton'
import { PricingFeaturesSection } from './PricingFeaturesSection'
import { pricingFieldsSchema, defaultPricingValues, type PricingFormFields } from '@/lib/schemas/pricing-fields'

interface NestedTRowFormProps {
  initialValues?: Partial<NestedTHangarParams>
  onValuesChange?: (values: NestedTHangarParams) => void
  onSave: (values: NestedTHangarParams, pricing?: PricingFormFields) => void
  onCancel: () => void
  isCreating?: boolean
  isSaving?: boolean
  previewLatitude?: number
  previewLongitude?: number
  isPlaced?: boolean
  areaId?: string
  areaName?: string
  onRemoved?: () => void
  initialPricing?: Partial<PricingFormFields>
}

export function NestedTRowForm({
  initialValues,
  onValuesChange,
  onSave,
  onCancel,
  isCreating = true,
  isSaving = false,
  previewLatitude,
  previewLongitude,
  isPlaced = true,
  areaId,
  areaName,
  onRemoved,
  initialPricing,
}: NestedTRowFormProps) {
  const isInitialMount = useRef(true)

  const {
    nestedTHangarFormValues,
        updateNestedTHangarFormValues,
      } = useBuildingEditorStore()

  const form = useForm<NestedTHangarParams>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(nestedTHangarParamsSchema) as unknown as Resolver<NestedTHangarParams>,
    defaultValues: {
      ...DEFAULT_NESTED_T_HANGAR_VALUES,
      ...initialValues,
      ...(nestedTHangarFormValues || {}),
    },
  })

  const pricingForm = useForm<PricingFormFields>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(pricingFieldsSchema) as unknown as Resolver<PricingFormFields>,
    defaultValues: { ...defaultPricingValues, ...initialPricing },
  })

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form

  const watchedValues = watch()

  // Bidirectional unit_width ↔ tail_width linking (2:1 ratio for nested interleaving)
  const handleUnitWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value)
    if (!isNaN(val)) {
      setValue('unit_width_ft', val)
      setValue('tail_width_ft', val / 2)
    }
  }

  const handleTailWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value)
    if (!isNaN(val)) {
      setValue('tail_width_ft', val)
      setValue('unit_width_ft', val * 2)
    }
  }

  // Row B count is always unit_count - 1
  const rowBCount = useMemo(() => getRowBCount(watchedValues), [watchedValues.unit_count])

  // Calculate layout with current values
  const layoutResult = useMemo(() => {
    return calculateNestedTHangarLayout(watchedValues)
  }, [
    watchedValues.unit_width_ft,
    watchedValues.wing_depth_ft,
    watchedValues.tail_depth_ft,
    watchedValues.tail_width_ft,
    watchedValues.unit_count,
    watchedValues.wall_thickness_ft,
    watchedValues.height_ft,
  ])

  // Derived values
  const stepWidth = useMemo(() => calculateStepWidth(watchedValues), [
    watchedValues.unit_width_ft, watchedValues.tail_width_ft,
  ])
  const unitPitch = useMemo(() => calculateNestedUnitPitch(watchedValues), [
    watchedValues.unit_width_ft, watchedValues.wall_thickness_ft,
  ])
  const unitArea = useMemo(() => calculateTShapeArea(watchedValues), [
    watchedValues.unit_width_ft, watchedValues.wing_depth_ft, watchedValues.tail_width_ft,
  ])

  // Sync form values to store for preview updates
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    updateNestedTHangarFormValues(watchedValues)
  }, [
    watchedValues.unit_width_ft,
    watchedValues.wing_depth_ft,
    watchedValues.tail_depth_ft,
    watchedValues.tail_width_ft,
    watchedValues.height_ft,
    watchedValues.unit_count,
    watchedValues.wall_thickness_ft,
    watchedValues.default_door_type,
    watchedValues.default_door_width_ft,
    watchedValues.default_door_height_ft,
    watchedValues.floor_color,
    watchedValues.wall_color,
    watchedValues.bearing_deg,
    watchedValues.left_end_mode,
    watchedValues.right_end_mode,
    watchedValues.name,
    watchedValues.code,
    JSON.stringify(watchedValues.row_a_overrides),
    JSON.stringify(watchedValues.row_b_overrides),
    updateNestedTHangarFormValues,
  ])

  // Sync bearing from store to form when changed externally (e.g., rotation ring)
  useEffect(() => {
    const storeBearing = nestedTHangarFormValues?.bearing_deg
    const formBearing = watchedValues.bearing_deg
    if (storeBearing !== undefined && Math.abs(storeBearing - formBearing) > 0.1) {
      setValue('bearing_deg', storeBearing)
    }
  }, [nestedTHangarFormValues?.bearing_deg, setValue])

  // Notify parent of value changes
  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(watchedValues)
    }
  }, [watchedValues, onValuesChange])

  // Handle unit count change
  const handleUnitCountChange = (value: number[]) => {
    setValue('unit_count', value[0])
    setValue('row_a_overrides', [])
    setValue('row_b_overrides', [])
  }

  // Per-unit override state and helpers
  const [openUnits, setOpenUnits] = useState<Set<string>>(new Set())

  const toggleUnit = (key: string) => {
    const newOpen = new Set(openUnits)
    if (newOpen.has(key)) newOpen.delete(key)
    else newOpen.add(key)
    setOpenUnits(newOpen)
  }

  const getRowAOverride = (index: number) => {
    return watchedValues.row_a_overrides?.find(o => o.index === index)
  }

  const updateRowAUnitDoors = (index: number, doors: UnitDoorsConfig) => {
    const currentOverrides = watchedValues.row_a_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)
    if (existingIndex >= 0) {
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], doors }
      setValue('row_a_overrides', updated)
    } else {
      setValue('row_a_overrides', [...currentOverrides, { index, doors }])
    }
  }

  const removeRowAOverride = (index: number) => {
    const currentOverrides = watchedValues.row_a_overrides || []
    setValue('row_a_overrides', currentOverrides.filter(o => o.index !== index))
  }

  const getEffectiveUnitDoors = (): UnitDoorsConfig => {
    const defaultDoor = createDefaultTHangarDoorSpec(watchedValues as any)
    return { front: [defaultDoor] }
  }

  const getEffectiveRowAUnitDoors = (index: number): UnitDoorsConfig => {
    const override = getRowAOverride(index)
    if (override?.doors) return override.doors
    return getEffectiveUnitDoors()
  }

  const copyDoorsToAllRowAUnits = (sourceIndex: number) => {
    const sourceDoors = getEffectiveRowAUnitDoors(sourceIndex)
    const currentOverrides = watchedValues.row_a_overrides || []
    const newOverrides = Array.from({ length: watchedValues.unit_count }, (_, i) => ({
      index: i,
      type: currentOverrides.find(o => o.index === i)?.type,
      doors: sourceDoors,
    }))
    setValue('row_a_overrides', newOverrides)
  }

  // Row B override helpers
  const getRowBOverride = (index: number) => {
    return watchedValues.row_b_overrides?.find(o => o.index === index)
  }

  const updateRowBUnitDoors = (index: number, doors: UnitDoorsConfig) => {
    const currentOverrides = watchedValues.row_b_overrides || []
    const existingIndex = currentOverrides.findIndex(o => o.index === index)
    if (existingIndex >= 0) {
      const updated = [...currentOverrides]
      updated[existingIndex] = { ...updated[existingIndex], doors }
      setValue('row_b_overrides', updated)
    } else {
      setValue('row_b_overrides', [...currentOverrides, { index, doors }])
    }
  }

  const removeRowBOverride = (index: number) => {
    const currentOverrides = watchedValues.row_b_overrides || []
    setValue('row_b_overrides', currentOverrides.filter(o => o.index !== index))
  }

  const getEffectiveRowBUnitDoors = (index: number): UnitDoorsConfig => {
    const override = getRowBOverride(index)
    if (override?.doors) return override.doors
    return getEffectiveUnitDoors()
  }

  const copyDoorsToAllRowBUnits = (sourceIndex: number) => {
    const sourceDoors = getEffectiveRowBUnitDoors(sourceIndex)
    const currentOverrides = watchedValues.row_b_overrides || []
    const newOverrides = Array.from({ length: rowBCount }, (_, i) => ({
      index: i,
      type: currentOverrides.find(o => o.index === i)?.type,
      doors: sourceDoors,
    }))
    setValue('row_b_overrides', newOverrides)
  }

  // Show position
  const latitude = previewLatitude?.toFixed(6) || watchedValues.latitude?.toFixed(6) || '—'
  const longitude = previewLongitude?.toFixed(6) || watchedValues.longitude?.toFixed(6) || '—'

  const onSubmit = async (values: NestedTHangarParams) => {
    const pricingValid = await pricingForm.trigger()
    if (!pricingValid) return

    if (previewLatitude !== undefined && previewLongitude !== undefined) {
      values.latitude = previewLatitude
      values.longitude = previewLongitude
    }
    onSave(values, pricingForm.getValues())
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full flex-1 min-h-0">
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-6">
          {/* Building Section Header */}
          <div className="bg-purple-50 dark:bg-purple-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-purple-700 dark:text-purple-300">Nested T-Hangar</h3>
            <p className="text-xs text-muted-foreground">Interlocking T-hangar rows with zipper tail pattern</p>
          </div>

          {/* Identity Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Identity</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" {...register('name')} placeholder="e.g., Nested T-Hangar A" />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input id="code" {...register('code')} placeholder="e.g., NTH-A" />
                {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
              </div>
            </div>
          </div>

          <Separator />

          {/* T-Shape Dimensions */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">T-Shape Dimensions (feet)</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="unit_width_ft">Unit Width (wing span)</Label>
                <Input
                  id="unit_width_ft"
                  type="number"
                  step="0.5"
                  value={watchedValues.unit_width_ft}
                  onChange={handleUnitWidthChange}
                />
                {errors.unit_width_ft && (
                  <p className="text-xs text-destructive">{errors.unit_width_ft.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tail_width_ft">Tail Width (= ½ unit width)</Label>
                <Input
                  id="tail_width_ft"
                  type="number"
                  step="0.5"
                  value={watchedValues.tail_width_ft}
                  onChange={handleTailWidthChange}
                />
                {errors.tail_width_ft && (
                  <p className="text-xs text-destructive">{errors.tail_width_ft.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="wing_depth_ft">Wing Depth</Label>
                <Input
                  id="wing_depth_ft"
                  type="number"
                  step="0.5"
                  {...register('wing_depth_ft', { valueAsNumber: true })}
                />
                {errors.wing_depth_ft && (
                  <p className="text-xs text-destructive">{errors.wing_depth_ft.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tail_depth_ft">Tail Depth</Label>
                <Input
                  id="tail_depth_ft"
                  type="number"
                  step="0.5"
                  {...register('tail_depth_ft', { valueAsNumber: true })}
                />
                {errors.tail_depth_ft && (
                  <p className="text-xs text-destructive">{errors.tail_depth_ft.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="height_ft">Height</Label>
                <Input
                  id="height_ft"
                  type="number"
                  step="0.5"
                  {...register('height_ft', { valueAsNumber: true })}
                />
                {errors.height_ft && (
                  <p className="text-xs text-destructive">{errors.height_ft.message}</p>
                )}
              </div>
            </div>

            {/* Wall Thickness */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="wall_thickness_ft">Wall Thickness (ft)</Label>
                <Input
                  id="wall_thickness_ft"
                  type="number"
                  step="0.25"
                  min={0.25}
                  max={2}
                  {...register('wall_thickness_ft', { valueAsNumber: true })}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Derived Values (read-only) */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-muted-foreground">Calculated Values</h4>
            <div className="bg-muted/50 rounded-md p-3 text-xs space-y-1.5">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Step width:</span>
                <span className="font-medium">{stepWidth.toFixed(1)} ft</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Unit pitch (c-to-c):</span>
                <span className="font-medium">{unitPitch.toFixed(1)} ft</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">T-shape area:</span>
                <span className="font-medium">{unitArea.toFixed(0)} sq ft</span>
              </div>
              <Separator className="my-1" />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Building depth:</span>
                <span className="font-medium">{layoutResult.buildingDepth.toFixed(1)} ft</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Building width:</span>
                <span className="font-medium">{layoutResult.buildingWidth.toFixed(1)} ft</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total units:</span>
                <span className="font-medium">{layoutResult.totalUnits}</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Unit Configuration */}
          <div className="bg-green-50 dark:bg-green-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-green-700 dark:text-green-300">Unit Configuration</h3>
          </div>

          <div className="space-y-4">
            {/* Unit Count (Row A) */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Unit Count (Row A)</Label>
                <span className="text-sm font-medium">{watchedValues.unit_count}</span>
              </div>
              <Slider
                value={[watchedValues.unit_count]}
                onValueChange={handleUnitCountChange}
                min={2}
                max={30}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>2</span>
                <span>30</span>
              </div>
              <div className="bg-muted/50 rounded-md px-3 py-2 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Row B units:</span>
                  <span className="font-medium">{rowBCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total units:</span>
                  <span className="font-medium">{2 * watchedValues.unit_count - 1}</span>
                </div>
              </div>
            </div>

            {/* Layout Preview */}
            <div className="space-y-2">
              <Label>Row Layout</Label>
              <NestedTHangarLayoutPreview
                rowACount={watchedValues.unit_count}
                rowBCount={rowBCount}
                unitWidth={watchedValues.unit_width_ft}
                tailWidth={watchedValues.tail_width_ft}
                leftEndMode={watchedValues.left_end_mode}
                rightEndMode={watchedValues.right_end_mode}
              />
            </div>

            {/* End-Space Mode — Per Side */}
            <div className="space-y-3">
              <Label>End-Space Handling</Label>
              <p className="text-xs text-muted-foreground">
                Row B has fewer units, leaving gaps at both ends of its wing zone.
              </p>

              {/* Left End */}
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Left End</span>
                <div className="flex rounded-md border overflow-hidden">
                  {([
                    { value: 'separate' as EndSpaceMode, label: 'Separate' },
                    { value: 'merge_row_a' as EndSpaceMode, label: '→ Row A' },
                    { value: 'merge_row_b' as EndSpaceMode, label: '→ Row B' },
                  ] as const).map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
                        watchedValues.left_end_mode === value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background hover:bg-muted'
                      }`}
                      onClick={() => setValue('left_end_mode', value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right End */}
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Right End</span>
                <div className="flex rounded-md border overflow-hidden">
                  {([
                    { value: 'separate' as EndSpaceMode, label: 'Separate' },
                    { value: 'merge_row_a' as EndSpaceMode, label: '→ Row A' },
                    { value: 'merge_row_b' as EndSpaceMode, label: '→ Row B' },
                  ] as const).map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
                        watchedValues.right_end_mode === value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background hover:bg-muted'
                      }`}
                      onClick={() => setValue('right_end_mode', value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-muted/50 rounded-md px-3 py-2 text-xs space-y-1">
                <div><span className="text-muted-foreground">End space:</span> {layoutResult.endSpaceWidth.toFixed(1)} x {layoutResult.endSpaceDepth.toFixed(1)} ft each side</div>
              </div>
            </div>

            {/* Warnings */}
            {layoutResult.warnings.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                {layoutResult.warnings.map((warning, i) => (
                  <p key={i} className="text-xs text-amber-700 dark:text-amber-300">{warning}</p>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Door Configuration */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Door Configuration</h4>
            <div className="space-y-2">
              <Label htmlFor="default_door_type">Door Type</Label>
              <Select
                value={watchedValues.default_door_type}
                onValueChange={(value) => setValue('default_door_type', value as DoorType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rolling">Rolling (20-60 ft)</SelectItem>
                  <SelectItem value="bifold">Bi-fold (30-100 ft)</SelectItem>
                  <SelectItem value="sliding">Sliding (40-200 ft)</SelectItem>
                  <SelectItem value="hydraulic">Hydraulic (50-150 ft)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="default_door_width_ft">Door Width (ft)</Label>
                <Input
                  id="default_door_width_ft"
                  type="number"
                  step="0.5"
                  {...register('default_door_width_ft', { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="default_door_height_ft">Door Height (ft)</Label>
                <Input
                  id="default_door_height_ft"
                  type="number"
                  step="0.5"
                  {...register('default_door_height_ft', { valueAsNumber: true })}
                />
              </div>
            </div>

            <div className="bg-muted/50 rounded-md px-3 py-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Effective door width:</span>
                <span className="font-medium">
                  {Math.min(watchedValues.default_door_width_ft || 0, (watchedValues.unit_width_ft || 0) - 4).toFixed(1)} ft
                </span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Individual Unit Adjustments */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Individual Unit Adjustments</h4>
            <p className="text-xs text-muted-foreground">Override door for specific units (Row A only; Row B mirrors defaults)</p>

            {/* Row A units */}
            <div className="space-y-1">
              <span className="text-xs font-medium text-purple-600 dark:text-purple-400">Row A ({watchedValues.unit_count} units, south-facing)</span>
              <div className="space-y-2">
                {Array.from({ length: watchedValues.unit_count }, (_, i) => {
                  const override = getRowAOverride(i)
                  const key = `A-${i}`
                  const isOpen = openUnits.has(key)
                  const hasDoorOverride = override?.doors !== undefined
                  const effectiveDoors = getEffectiveRowAUnitDoors(i)
                  const frontDoor = effectiveDoors.front?.[0]

                  return (
                    <Collapsible key={key} open={isOpen} onOpenChange={() => toggleUnit(key)}>
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            'w-full flex items-center justify-between p-2 rounded-md text-sm',
                            'hover:bg-muted/50 transition-colors',
                            hasDoorOverride && 'bg-amber-50 dark:bg-amber-950/30'
                          )}
                        >
                          <div className="flex items-center gap-2">
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <span>A-{i + 1}</span>
                            {hasDoorOverride && (
                              <span className="text-xs text-amber-600 dark:text-amber-400">(custom)</span>
                            )}
                          </div>
                          <span className="text-muted-foreground text-xs">
                            {frontDoor ? `${frontDoor.width_ft.toFixed(0)}×${frontDoor.height_ft.toFixed(0)} ft` : 'No door'}
                          </span>
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-3 border rounded-md mt-1 space-y-4">
                          <THangarUnitDoorEditor
                            unitIndex={i}
                            unitCount={watchedValues.unit_count}
                            doors={getEffectiveRowAUnitDoors(i)}
                            onChange={(doors) => updateRowAUnitDoors(i, doors)}
                            unitWidthFt={watchedValues.unit_width_ft}
                            unitHeightFt={watchedValues.height_ft}
                            onCopyToAll={() => copyDoorsToAllRowAUnits(i)}
                          />
                          {hasDoorOverride && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-xs text-muted-foreground"
                              onClick={() => removeRowAOverride(i)}
                            >
                              Reset to default
                            </Button>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )
                })}
              </div>
            </div>

            {/* Row B units */}
            <div className="space-y-1">
              <span className="text-xs font-medium text-rose-600 dark:text-rose-400">Row B ({rowBCount} units, north-facing)</span>
              <div className="space-y-2">
                {Array.from({ length: rowBCount }, (_, j) => {
                  const override = getRowBOverride(j)
                  const key = `B-${j}`
                  const isOpen = openUnits.has(key)
                  const hasDoorOverride = override?.doors !== undefined
                  const effectiveDoors = getEffectiveRowBUnitDoors(j)
                  const frontDoor = effectiveDoors.front?.[0]

                  return (
                    <Collapsible key={key} open={isOpen} onOpenChange={() => toggleUnit(key)}>
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            'w-full flex items-center justify-between p-2 rounded-md text-sm',
                            'hover:bg-muted/50 transition-colors',
                            hasDoorOverride && 'bg-amber-50 dark:bg-amber-950/30'
                          )}
                        >
                          <div className="flex items-center gap-2">
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <span>B-{j + 1}</span>
                            {hasDoorOverride && (
                              <span className="text-xs text-amber-600 dark:text-amber-400">(custom)</span>
                            )}
                          </div>
                          <span className="text-muted-foreground text-xs">
                            {frontDoor ? `${frontDoor.width_ft.toFixed(0)}×${frontDoor.height_ft.toFixed(0)} ft` : 'No door'}
                          </span>
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-3 border rounded-md mt-1 space-y-4">
                          <THangarUnitDoorEditor
                            unitIndex={j}
                            unitCount={rowBCount}
                            doors={getEffectiveRowBUnitDoors(j)}
                            onChange={(doors) => updateRowBUnitDoors(j, doors)}
                            unitWidthFt={watchedValues.unit_width_ft}
                            unitHeightFt={watchedValues.height_ft}
                            onCopyToAll={() => copyDoorsToAllRowBUnits(j)}
                          />
                          {hasDoorOverride && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-xs text-muted-foreground"
                              onClick={() => removeRowBOverride(j)}
                            >
                              Reset to default
                            </Button>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )
                })}
              </div>
            </div>
          </div>

          <Separator />

          {/* Appearance */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Appearance</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="floor_color">Floor Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="floor_color"
                    type="color"
                    className="w-10 h-9 p-1 cursor-pointer"
                    {...register('floor_color')}
                  />
                  <Input
                    type="text"
                    className="flex-1"
                    {...register('floor_color')}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wall_color">Wall Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="wall_color"
                    type="color"
                    className="w-10 h-9 p-1 cursor-pointer"
                    {...register('wall_color')}
                  />
                  <Input
                    type="text"
                    className="flex-1"
                    {...register('wall_color')}
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Position */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Position</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Latitude</Label>
                <p className="text-xs font-mono bg-muted/50 px-2 py-1.5 rounded">{latitude}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Longitude</Label>
                <p className="text-xs font-mono bg-muted/50 px-2 py-1.5 rounded">{longitude}</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bearing_deg">Bearing (degrees)</Label>
              <Input
                id="bearing_deg"
                type="number"
                step="any"
                {...register('bearing_deg', { valueAsNumber: true })}
              />
            </div>
          </div>

          <Separator />

          {/* Pricing & Features */}
          <PricingFeaturesSection form={pricingForm} />

          {/* Delete button (edit mode only) */}
          {areaId && (
            <>
              <Separator />
              <RemoveAreaButton
                areaId={areaId}
                areaName={areaName}
                onRemoved={onRemoved}
              />
            </>
          )}
        </div>
      </ScrollArea>

      {/* Action Buttons */}
      <div className="border-t px-4 py-3 flex gap-2 shrink-0">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1"
          disabled={isSaving || (!isPlaced && isCreating)}
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : isCreating ? (
            'Create Nested T-Hangar'
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </form>
  )
}

// ============================================================================
// Inline Nested T-Hangar Layout Preview SVG — Interleaving Pattern
// ============================================================================

function NestedTHangarLayoutPreview({
  rowACount,
  rowBCount,
  unitWidth,
  tailWidth,
  leftEndMode = 'separate',
  rightEndMode = 'separate',
}: {
  rowACount: number
  rowBCount: number
  unitWidth: number
  tailWidth: number
  leftEndMode?: EndSpaceMode
  rightEndMode?: EndSpaceMode
}) {
  const stepWidth = (unitWidth - tailWidth) / 2
  const pitch = unitWidth // Same-direction pitch (no alternating)

  const svgWidth = 380
  const svgHeight = 120
  const padding = 12

  // Three zones: Row A wings, interleaving tails, Row B wings
  const zoneHeight = (svgHeight - 2 * padding) / 3

  // Scale to fit Row A (wider row)
  const totalRowWidth = (rowACount - 1) * pitch + unitWidth
  const scale = (svgWidth - 2 * padding) / totalRowWidth

  const tw = tailWidth * scale
  const sw = stepWidth * scale
  const w = unitWidth * scale
  const halfPitch = (pitch * scale) / 2

  // Layout (top to bottom): B wings → tails → A wings
  // This matches the 3D rendering where Row A (south-facing) is at bottom

  // Row B: north-facing, wings at top, tails going down into middle zone
  const rowBWings = Array.from({ length: rowBCount }, (_, j) => {
    const x = padding + halfPitch + j * pitch * scale
    return (
      <g key={`B-${j}`}>
        {/* Wing section (top) */}
        <rect x={x} y={padding} width={w} height={zoneHeight} fill="#fda4af" stroke="#f43f5e" strokeWidth={0.5} />
        {/* Tail section (going down into middle zone) */}
        <rect x={x + sw} y={padding + zoneHeight} width={tw} height={zoneHeight} fill="#fda4af" stroke="#f43f5e" strokeWidth={0.5} />
        <text x={x + w / 2} y={padding + zoneHeight / 2 + 3} textAnchor="middle" fontSize={7} fill="#f43f5e" fontWeight="bold">
          B{j + 1}
        </text>
      </g>
    )
  })

  // Row A: south-facing, wings at bottom, tails going up into middle zone
  const rowAWings = Array.from({ length: rowACount }, (_, i) => {
    const x = padding + i * pitch * scale
    return (
      <g key={`A-${i}`}>
        {/* Tail section (going up into middle zone) */}
        <rect x={x + sw} y={padding + zoneHeight} width={tw} height={zoneHeight} fill="#c4b5fd" stroke="#8b5cf6" strokeWidth={0.5} />
        {/* Wing section (bottom) */}
        <rect x={x} y={padding + 2 * zoneHeight} width={w} height={zoneHeight} fill="#c4b5fd" stroke="#8b5cf6" strokeWidth={0.5} />
        <text x={x + w / 2} y={padding + 2 * zoneHeight + zoneHeight / 2 + 3} textAnchor="middle" fontSize={7} fill="#8b5cf6" fontWeight="bold">
          A{i + 1}
        </text>
      </g>
    )
  })

  // End-space gaps — L-shaped: wing zone gap (top) + tail zone gap (middle)
  const gapColor = (mode: EndSpaceMode) => ({
    fill: mode === 'separate' ? '#bfdbfe' : mode === 'merge_row_a' ? '#c4b5fd' : '#fda4af',
    stroke: mode === 'separate' ? '#3b82f6' : mode === 'merge_row_a' ? '#8b5cf6' : '#f43f5e',
  })
  const leftGap = gapColor(leftEndMode)
  const rightGap = gapColor(rightEndMode)

  // Wing zone gap positions (in B wings zone at top)
  const leftWingGapX = padding
  const rightWingGapX = padding + (rowACount - 1) * pitch * scale + w - halfPitch
  const wingGapY = padding

  // Tail zone gap positions (in tails zone, middle)
  // Tail zone gap is narrower (stepWidth) — between A1's tail left edge and building wall
  const tailGapY = padding + zoneHeight
  const leftTailGapX = padding
  const rightTailGapX = padding + (rowACount - 1) * pitch * scale + w - sw

  return (
    <div className="border rounded-md bg-muted/30 p-1">
      <svg width={svgWidth} height={svgHeight} className="w-full">
        {/* Zone labels */}
        <text x={3} y={padding + zoneHeight / 2 + 3} fontSize={7} fill="#6b7280">B wings</text>
        <text x={3} y={padding + zoneHeight + zoneHeight / 2 + 3} fontSize={7} fill="#6b7280">tails</text>
        <text x={3} y={padding + 2 * zoneHeight + zoneHeight / 2 + 3} fontSize={7} fill="#6b7280">A wings</text>
        {rowBWings}
        {rowAWings}
        {/* End-space gaps — L-shape per side (wing zone + tail zone) */}
        {/* LEFT: wing zone gap (wider, in B wings zone) */}
        <rect x={leftWingGapX} y={wingGapY} width={halfPitch} height={zoneHeight}
          fill={leftGap.fill} stroke={leftGap.stroke} strokeWidth={0.5} strokeDasharray={leftEndMode === 'separate' ? '2,1' : undefined} />
        {/* LEFT: tail zone gap (narrower, in tails zone) */}
        <rect x={leftTailGapX} y={tailGapY} width={sw} height={zoneHeight}
          fill={leftGap.fill} stroke={leftGap.stroke} strokeWidth={0.5} strokeDasharray={leftEndMode === 'separate' ? '2,1' : undefined} />
        {/* RIGHT: wing zone gap */}
        <rect x={rightWingGapX} y={wingGapY} width={halfPitch} height={zoneHeight}
          fill={rightGap.fill} stroke={rightGap.stroke} strokeWidth={0.5} strokeDasharray={rightEndMode === 'separate' ? '2,1' : undefined} />
        {/* RIGHT: tail zone gap */}
        <rect x={rightTailGapX} y={tailGapY} width={sw} height={zoneHeight}
          fill={rightGap.fill} stroke={rightGap.stroke} strokeWidth={0.5} strokeDasharray={rightEndMode === 'separate' ? '2,1' : undefined} />
        {leftEndMode === 'separate' && (
          <text x={leftWingGapX + halfPitch / 2} y={wingGapY + zoneHeight / 2 + 3} textAnchor="middle" fontSize={5} fill="#3b82f6">END</text>
        )}
        {rightEndMode === 'separate' && (
          <text x={rightWingGapX + halfPitch / 2} y={wingGapY + zoneHeight / 2 + 3} textAnchor="middle" fontSize={5} fill="#3b82f6">END</text>
        )}
      </svg>
    </div>
  )
}
